// Apply a member's personalization as overrides of semantic tokens on the active theme
// (REQ-THEME-001/006). A member's accent/font become inline custom-property overrides on the
// document root; the corrected accent always comes from the AA-safe core (personalize.ts), so an
// applied theme is never below AA. Pure builder + a thin DOM apply/clear (jsdom-testable).
import { correctAccent, correctAccentBoth, type AccentBoth, type Theme } from "../design/personalize";
import { hexToRgb } from "../design/contrast";

export interface MemberPrefs {
  accent?: string | null;
  accent_corrected?: AccentBoth | null;
  font?: string | null;
  background_path?: string | null;
  derived_palette?: string[] | null;
}

// Curated, self-hosted font families (the @font-face rules ship in commit 5). Only these apply;
// anything else is ignored (REQ-THEME-002).
export const FONT_STACKS: Record<string, string> = {
  system: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  atkinson: '"Atkinson Hyperlegible", system-ui, sans-serif',
  nunito: '"Nunito", system-ui, sans-serif',
  lora: '"Lora", Georgia, serif',
};

// ── Curated Themes (REQ-THEME-001↺) — the primary personalization, replacing the free accent. ──
// Each preset is one base accent; the AA-correction core (personalize.ts) tunes it per active
// theme, so a Theme is never below AA. The base hex is stored in member_prefs.accent (reusing the
// existing column + accent_corrected), so no schema change — a Theme is just a curated accent.
export interface ThemePreset {
  name: string;
  label: string;
  base: string; // the preset's base accent (light); corrected per theme on apply
}
export const THEME_PRESETS: ThemePreset[] = [
  { name: "calm", label: "Calm", base: "#3a55c9" },
  { name: "warm", label: "Warm", base: "#c9603a" },
  { name: "bright", label: "Bright", base: "#0f9a8f" },
  { name: "playful", label: "Playful", base: "#7a49d6" },
];

export function themePreset(name: string | null | undefined): ThemePreset | undefined {
  return THEME_PRESETS.find((p) => p.name === name);
}

/** The AA-corrected token overrides for a preset on the active theme. */
export function themePresetOverrides(name: string, theme: Theme): Record<string, string> {
  const p = themePreset(name);
  if (!p) return {};
  const pair = correctAccent(p.base, theme);
  return { "--color-accent": pair.accent, "--color-accent-contrast": pair.onAccent };
}

/** Snap any stored hex to the nearest preset name (migrates a free 004 accent → a Theme). */
export function nearestTheme(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  let best = THEME_PRESETS[0];
  let bestD = Infinity;
  for (const p of THEME_PRESETS) {
    const [pr, pg, pb] = hexToRgb(p.base);
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best.name;
}

/** What picking a Theme writes to member_prefs: the preset's base accent + its AA-safe pair. */
export function buildThemePrefsUpdate(name: string): { accent: string; accent_corrected: AccentBoth } {
  const p = themePreset(name) ?? THEME_PRESETS[0];
  return buildAccentPrefsUpdate(p.base);
}

/** The custom-property overrides for a member's prefs on the active theme. AA-safe by construction. */
export function themeOverrides(prefs: MemberPrefs, theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};
  if (prefs.accent) {
    const pair = prefs.accent_corrected?.[theme] ?? correctAccent(prefs.accent, theme);
    out["--color-accent"] = pair.accent;
    out["--color-accent-contrast"] = pair.onAccent;
  }
  if (prefs.font && FONT_STACKS[prefs.font]) {
    out["--font-family"] = FONT_STACKS[prefs.font];
  }
  return out;
}

// Every property personalization may set — cleared before re-applying, and on sign-out.
export const OVERRIDE_KEYS = ["--color-accent", "--color-accent-contrast", "--font-family"] as const;

export function applyOverrides(root: HTMLElement, overrides: Record<string, string>): void {
  for (const k of OVERRIDE_KEYS) root.style.removeProperty(k); // reset first (dropped prefs go back to token defaults)
  for (const [k, v] of Object.entries(overrides)) root.style.setProperty(k, v);
}

/** Revert to the token defaults (e.g. on sign-out). */
export function clearOverrides(root: HTMLElement): void {
  for (const k of OVERRIDE_KEYS) root.style.removeProperty(k);
}

/** What the accent picker writes to member_prefs: the raw pick + the AA-safe pair for both
 *  themes, precomputed so the render path never applies an uncorrected colour (REQ-THEME-001). */
export function buildAccentPrefsUpdate(hex: string): { accent: string; accent_corrected: AccentBoth } {
  return { accent: hex, accent_corrected: correctAccentBoth(hex) };
}
